import React from "react";
import { cn } from "@/lib/utils";
import { ago } from "@/lib/format";
import { AlertTriangle, CheckCircle2, Clock, Info, Database } from "lucide-react";

// ===== StatusBadge — one restrained, Stripe-quality label system for all of Ezisell =====
const BADGE_TONES = {
  neutral: { color: "#596579", background: "#F5F7FA", borderColor: "#E3E8EF", dot: "#8792A2" },
  success: { color: "#087A55", background: "#ECFDF5", borderColor: "#BFE9D6", dot: "#10A778" },
  warning: { color: "#9A6700", background: "#FFF8E6", borderColor: "#F2D99B", dot: "#D99A16" },
  danger: { color: "#B42318", background: "#FFF2F0", borderColor: "#F1C0BA", dot: "#D92D20" },
  info: { color: "#315A8A", background: "#F1F6FC", borderColor: "#CCDCEF", dot: "#4F7CAC" },
};

/**
 * Walmart-yellow rating star. ONLY for ratings/reviews/review scores — never
 * for favorites, bookmarks, decorative stars or feature icons. The star is
 * decorative (aria-hidden); the numeric rating stays as visible text so the
 * value is never conveyed by color alone.
 */
export function RatingStar({ className }) {
  return (
    <span className={cn("text-walmart-yellow", className)} aria-hidden="true">
      ★
    </span>
  );
}

export function StatusBadge({ tone = "neutral", dot = true, icon, children, className, testid, title }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral;
  return (
    <span data-testid={testid} title={title}
      className={cn("inline-flex items-center gap-1.5 rounded-md border px-[7px] text-[12px] font-medium leading-none whitespace-nowrap align-middle", className)}
      style={{ color: t.color, background: t.background, borderColor: t.borderColor, minHeight: 22, paddingTop: 3, paddingBottom: 3 }}>
      {icon ? <span className="inline-flex items-center [&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
        : dot && <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: t.dot }} />}
      {children}
    </span>
  );
}

// Provenance tag — quiet, semantically consistent. "Fixture" is provenance (neutral), not a warning.
const PROV = {
  observed: { tone: "success", label: "Observed" },
  calculated: { tone: "neutral", label: "Calculated" },
  estimated: { tone: "warning", label: "Estimated" },
  "user-entered": { tone: "neutral", label: "User-entered" },
  ai: { tone: "info", label: "AI" },
  fixture: { tone: "neutral", label: "Demo" },
};

export function ProvenanceTag({ kind = "fixture", className, label }) {
  const p = PROV[kind] || PROV.fixture;
  return <StatusBadge tone={p.tone} className={className}>{label || p.label}</StatusBadge>;
}

export function FreshnessIndicator({ collectedAt, status = "fixture", className }) {
  const stale = status?.includes("stale");
  return (
    <span className={cn("inline-flex items-center gap-1 text-micro", stale ? "text-amber-600" : "text-ink-muted", className)}>
      <Clock className="h-3 w-3" />
      {collectedAt ? ago(collectedAt) : "—"}{stale ? " · stale" : ""}
    </span>
  );
}

export function SectionTitle({ children, right, className }) {
  return (
    <div className={cn("flex items-center justify-between mb-3", className)}>
      <h2 className="text-stitle text-ink-primary">{children}</h2>
      {right}
    </div>
  );
}

const VERDICT_STYLES = {
  "STRONG BUY": { rail: "bg-emerald-600", text: "text-emerald-600", bg: "bg-emerald-100", icon: CheckCircle2 },
  BUY: { rail: "bg-emerald-500", text: "text-emerald-600", bg: "bg-emerald-100", icon: CheckCircle2 },
  "TEST BUY": { rail: "bg-signal-blue", text: "text-signal-blue", bg: "bg-signal-blue/10", icon: CheckCircle2 },
  REVIEW: { rail: "bg-amber-600", text: "text-amber-600", bg: "bg-amber-100", icon: Clock },
  WATCH: { rail: "bg-amber-600", text: "text-amber-600", bg: "bg-amber-100", icon: Clock },
  AVOID: { rail: "bg-red-600", text: "text-red-600", bg: "bg-red-100", icon: AlertTriangle },
  REJECT: { rail: "bg-red-600", text: "text-red-600", bg: "bg-red-100", icon: AlertTriangle },
  INCOMPLETE: { rail: "bg-ink-muted", text: "text-ink-secondary", bg: "bg-surface-2", icon: Info },
};

const VERDICT_TONE = {
  "STRONG BUY": "success", BUY: "success", "TEST BUY": "info",
  REVIEW: "warning", WATCH: "warning", AVOID: "danger", REJECT: "danger", INCOMPLETE: "neutral",
};
const titleCase = (s) => (s ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s);

export function VerdictBadge({ verdict, className }) {
  const s = VERDICT_STYLES[verdict] || VERDICT_STYLES.INCOMPLETE;
  const Icon = s.icon;
  return (
    <StatusBadge data-testid={`verdict-badge-${verdict?.toLowerCase().replace(/ /g, "-")}`}
      testid={`verdict-badge-${verdict?.toLowerCase().replace(/ /g, "-")}`}
      tone={VERDICT_TONE[verdict] || "neutral"} dot={false} icon={<Icon />} className={className}>
      {titleCase(verdict)}
    </StatusBadge>
  );
}

export const verdictStyle = (v) => VERDICT_STYLES[v] || VERDICT_STYLES.INCOMPLETE;

export function EmptyState({ icon: Icon = Database, title, hint, action, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center text-center py-14 px-6">
      <div className="h-10 w-10 rounded-panel bg-surface-2 flex items-center justify-center mb-3">
        <Icon className="h-5 w-5 text-ink-muted" />
      </div>
      <p className="text-body font-medium text-ink-primary">{title}</p>
      {hint && <p className="text-compact text-ink-muted mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className, style }) {
  return <div style={style} className={cn("ez-shimmer rounded-ctrl", className)} />;
}

export function TableSkeleton({ rows = 8, cols = 5 }) {
  return (
    <div className="space-y-2" data-testid="table-skeleton">
      <Skeleton className="h-8 w-full" />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-3">
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton key={j} className="h-5" style={{ width: `${[28, 18, 14, 20, 16][j % 5]}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function Metric({ label, value, sub, emphasis, negative, flash, testid }) {
  return (
    <div data-testid={testid} className={cn("px-4 py-2.5", flash && "ez-value-flash")}>
      <div className="text-micro uppercase tracking-wide text-ink-muted font-mono">{label}</div>
      <div className={cn("font-mono tabnum mt-0.5", emphasis ? "text-metric" : "text-metricsm",
        negative ? "text-red-600" : "text-ink-primary")}>{value}</div>
      {sub && <div className="text-micro text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

export function Panel({ children, className, testid }) {
  return (
    <div data-testid={testid} className={cn("bg-surface-0 border border-edge-subtle rounded-panel shadow-float", className)}>
      {children}
    </div>
  );
}
